import { z } from 'zod';
import { COUNTRIES } from './countries.js';
/** The branch answer that says the candidate never served. */
export const BRANCH_NONE = 'none';
const BRANCHES = [
    { value: 'army', label: 'Army' },
    { value: 'navy', label: 'Navy' },
    { value: 'air_force', label: 'Air Force' },
    { value: 'marines', label: 'Marines' },
    { value: 'special_forces', label: 'Special Forces' },
    { value: 'cyber', label: 'Cyber' },
    { value: 'medical', label: 'Medical' },
    { value: 'joint_other', label: 'Joint/Other' },
    { value: BRANCH_NONE, label: 'No military service' },
];
export const QUESTIONS = [
    {
        id: 'country_from',
        label: 'Which country are you from?',
        shortLabel: 'From',
        filterLabel: 'From',
        type: 'single',
        options: COUNTRIES,
        required: true,
        filter: true,
    },
    {
        id: 'citizenships',
        label: 'Which citizenship(s) do you hold?',
        shortLabel: 'Citizenship',
        filterLabel: 'Citizenship',
        type: 'multi',
        options: COUNTRIES,
        required: true,
        filter: true,
    },
    {
        id: 'countries_experience',
        label: 'In which countries have you served in the military and/or worked?',
        shortLabel: 'Served in',
        filterLabel: 'Experience in',
        type: 'multi',
        options: COUNTRIES,
        required: true,
        filter: true,
    },
    {
        id: 'branch',
        label: 'Which branch did you serve in?',
        shortLabel: 'Branch',
        filterLabel: 'Branch',
        type: 'single',
        options: BRANCHES,
        required: true,
        filter: true,
    },
    {
        id: 'roles',
        label: 'What were your roles? Please be as specific as possible.',
        shortLabel: 'Roles',
        type: 'text',
        multiline: true,
        required: true,
        showWhen: {
            questionId: 'branch',
            anyOf: BRANCHES.flatMap((branch) => (branch.value === BRANCH_NONE ? [] : [branch.value])),
        },
    },
    {
        id: 'expertise',
        label: 'Which areas best describe your military knowledge and experience?',
        shortLabel: 'Expertise',
        filterLabel: 'Expertise',
        type: 'multi',
        options: [
            { value: 'operations', label: 'Operations' },
            { value: 'logistics', label: 'Logistics' },
            { value: 'intelligence', label: 'Intelligence' },
            { value: 'procurement_acquisition', label: 'Procurement & acquisition' },
            { value: 'communications_it', label: 'Communications & IT' },
            { value: 'engineering', label: 'Engineering' },
            { value: 'command_leadership', label: 'Command & leadership' },
            { value: 'training', label: 'Training' },
            { value: 'planning_strategy', label: 'Planning & strategy' },
            { value: 'medical', label: 'Medical' },
            { value: 'cyber', label: 'Cyber' },
            { value: 'aviation', label: 'Aviation' },
            { value: 'maritime', label: 'Maritime' },
            { value: 'other', label: 'Other' },
        ],
        required: true,
        filter: true,
    },
    {
        id: 'expertise_other',
        label: 'Other areas of expertise',
        shortLabel: 'Other expertise',
        type: 'text',
        required: false,
        showWhen: { questionId: 'expertise', anyOf: ['other'] },
    },
    {
        id: 'deployed_abroad',
        label: 'Have you been deployed abroad?',
        shortLabel: 'Deployed abroad',
        filterLabel: 'Deployed abroad',
        type: 'yes_no',
        required: true,
        filter: true,
    },
    {
        id: 'deployments',
        label: 'If so, where and for how long?',
        shortLabel: 'Deployments',
        type: 'text',
        multiline: true,
        required: true,
        showWhen: { questionId: 'deployed_abroad', anyOf: ['yes'] },
    },
    {
        // Optional until Michael confirms it may be asked at all. Yes or no only:
        // the level and issuing country are for a company to ask the candidate,
        // not for the pool to hold.
        id: 'clearance',
        label: 'Do you currently hold any active security clearance?',
        shortLabel: 'Clearance',
        filterLabel: 'Clearance',
        type: 'yes_no',
        required: false,
        filter: true,
    },
    {
        id: 'positions',
        label: 'What positions or types of roles are you looking for?',
        shortLabel: 'Positions',
        filterLabel: 'Looking for',
        type: 'multi',
        options: [
            { value: 'operations', label: 'Operations' },
            { value: 'business_development_sales', label: 'Business development & sales' },
            { value: 'programme_project_management', label: 'Programme / project management' },
            { value: 'procurement_government_affairs', label: 'Procurement & government affairs' },
            { value: 'strategy_chief_of_staff', label: 'Strategy & chief of staff' },
            { value: 'finance', label: 'Finance' },
            { value: 'product', label: 'Product' },
            { value: 'engineering', label: 'Engineering' },
            { value: 'other', label: 'Other' },
        ],
        required: true,
        filter: true,
    },
    {
        id: 'positions_other',
        label: 'Other positions',
        shortLabel: 'Other positions',
        type: 'text',
        required: false,
        showWhen: { questionId: 'positions', anyOf: ['other'] },
    },
];
export const FILTER_QUESTIONS = QUESTIONS.flatMap((question) => {
    switch (question.type) {
        case 'yes_no':
        case 'single':
        case 'multi':
            return question.filter === true ? [question] : [];
        case 'text':
        case 'month':
            return [];
        default:
            return unreachable(question);
    }
});
export const QUESTION_TEXT_MAX_CHARS = 300;
/** A multi-line answer asks for detail, so it is given room for it. */
export const QUESTION_LONG_TEXT_MAX_CHARS = 2000;
export function textMaxChars(question) {
    return question.multiline === true ? QUESTION_LONG_TEXT_MAX_CHARS : QUESTION_TEXT_MAX_CHARS;
}
export const YES_NO_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
/** The values a question with a closed vocabulary takes; null for a free one. */
export function questionValues(question) {
    switch (question.type) {
        case 'single':
        case 'multi':
            return question.options.map((option) => option.value);
        case 'yes_no':
            return YES_NO_OPTIONS.map((option) => option.value);
        case 'text':
        case 'month':
            return null;
        default:
            return unreachable(question);
    }
}
function unreachable(value) {
    throw new Error(`Unhandled question: ${JSON.stringify(value)}`);
}
/** A form sends an untouched field as empty; that is an unanswered question, not a wrong answer. */
function blankToUndefined(value) {
    if (typeof value === 'string' && value.trim() === '')
        return undefined;
    if (Array.isArray(value) && value.length === 0)
        return undefined;
    return value;
}
export const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
function oneOf(values, message) {
    return z.string({ error: message }).refine((value) => values.includes(value), message);
}
function answerSchema(question) {
    const label = `"${question.label}"`;
    switch (question.type) {
        case 'text':
            return z.preprocess(blankToUndefined, z
                .string({ error: `Answer ${label} in words.` })
                .trim()
                .max(textMaxChars(question), `Keep ${label} under ${textMaxChars(question)} characters.`)
                .optional());
        case 'month':
            return z.preprocess(blankToUndefined, z
                .string({ error: `Give ${label} as a month.` })
                .regex(MONTH_PATTERN, `Give ${label} as a month.`)
                .optional());
        case 'yes_no':
        case 'single':
            return z.preprocess(blankToUndefined, oneOf(questionValues(question) ?? [], `Choose one of the options for ${label}.`).optional());
        case 'multi':
            return z.preprocess(blankToUndefined, z
                .array(oneOf(questionValues(question) ?? [], `Choose from the options for ${label}.`), {
                error: `Choose from the options for ${label}.`,
            })
                .max(question.options.length)
                .transform((values) => [...new Set(values)])
                .optional());
        default:
            return unreachable(question);
    }
}
/** Whether the form asks this question, given the answers before it. */
export function isAsked(question, answers) {
    if (question.showWhen === undefined)
        return true;
    const { questionId, anyOf } = question.showWhen;
    const given = answers[questionId];
    if (given === undefined)
        return false;
    const values = typeof given === 'string' ? [given] : given;
    return values.some((value) => anyOf.includes(value));
}
/**
 * The answers as stored: unknown ids refused, blanks dropped, and the
 * follow-ups of an answer that does not ask them dropped too (a "no" to
 * service keeps no branch the form still held).
 */
export const questionnaireAnswersSchema = z
    .strictObject(Object.fromEntries(QUESTIONS.map((question) => [question.id, answerSchema(question)])), {
    error: (issue) => issue.code === 'unrecognized_keys'
        ? 'An answer to a question this form does not ask.'
        : undefined,
})
    .transform((answers) => {
    const kept = {};
    for (const question of QUESTIONS) {
        const value = answers[question.id];
        if (value !== undefined && isAsked(question, kept))
            kept[question.id] = value;
    }
    return kept;
});
/**
 * What free-text search reads of the answers: the labels a person would type
 * ("Germany", "Logistics") rather than the codes stored, and the candidate's
 * own words. A yes or no is left out: "yes" alone says nothing to search for.
 */
export function answersSearchText(answers) {
    const parts = [];
    for (const question of QUESTIONS) {
        const value = answers[question.id];
        if (value === undefined)
            continue;
        const values = typeof value === 'string' ? [value] : value;
        switch (question.type) {
            case 'single':
            case 'multi':
                for (const chosen of values) {
                    parts.push(question.options.find((option) => option.value === chosen)?.label ?? chosen);
                }
                break;
            case 'text':
            case 'month':
                parts.push(...values);
                break;
            case 'yes_no':
                break;
            default:
                unreachable(question);
        }
    }
    return parts.join(' ');
}
/** Listed to companies only when this holds: every required question it was asked is answered. */
export function isComplete(answers) {
    return QUESTIONS.every((question) => !question.required || !isAsked(question, answers) || answers[question.id] !== undefined);
}
/**
 * A school address stops working when the candidate leaves, and the pool is
 * meant to reach them years later. An entry ending in a dot is a label the
 * domain contains anywhere ("ac." covers ox.ac.uk and u-tokyo.ac.jp, "edu."
 * covers mit.edu and unimelb.edu.au); any other entry is a domain suffix. It
 * only warns, so a false match costs a sentence on the page.
 */
export function isSchoolEmail(email, schoolDomains) {
    const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();
    const labels = domain.split('.');
    return schoolDomains.some((entry) => {
        const lowered = entry.toLowerCase();
        if (lowered.endsWith('.'))
            return labels.includes(lowered.slice(0, -1));
        return domain === lowered || domain.endsWith(`.${lowered}`);
    });
}
//# sourceMappingURL=questionnaire.js.map